import Point from "@arcgis/core/geometry/Point";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import FeatureFilter from "@arcgis/core/layers/support/FeatureFilter";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import MapView from "@arcgis/core/views/MapView";
import { getHighlightMarkerSymbolProps } from "@components/world-map/world-map-constants";
import { MapStyle } from "@components/world-map/world-map-types";
import { isClusterGraphic } from "@components/world-map/world-map-utils";

export const MARKER_PULSE_PROPERTIES = {
  /** Resting size multiplier kept while the marker is highlighted (marker stays slightly bigger). */
  highlightScale: 1.15,

  /** Duration of a single pulse (grow and back down) in milliseconds. */
  pulseDurationInMs: 800,

  /** Pause between consecutive pulses in milliseconds. */
  pulseRestInMs: 250,

  /** Extra size added at the peak of the pulse, on top of the settle scale. */
  popScale: 0.25,
};

/** Returns the highlight overlay symbol matching the given map style, sized at the resting scale. */
export function getHighlightSymbol(
  mapStyle: MapStyle,
  scale: number = MARKER_PULSE_PROPERTIES.highlightScale
): PictureMarkerSymbol {
  return new PictureMarkerSymbol(
    getHighlightMarkerSymbolProps(mapStyle, scale)
  );
}

/**
 * Continuously animates a highlight overlay graphic with a repeating pulse: the
 * marker grows slightly bigger and shrinks back in place (no vertical movement),
 * pauses briefly, then repeats. Pulsing in place keeps a cluster's centered count
 * label aligned with the marker and gives single markers the same motion.
 *
 * The loop runs until the returned canceller is called, which resets the symbol
 * to its resting size.
 */
export function animateHighlightPop(
  graphic: __esri.Graphic,
  mapStyle: MapStyle
): () => void {
  const start = performance.now();
  const cycleMs =
    MARKER_PULSE_PROPERTIES.pulseDurationInMs +
    MARKER_PULSE_PROPERTIES.pulseRestInMs;
  let frameHandle: number | null = null;

  function tick(now: number): void {
    const phase = (now - start) % cycleMs;
    // Smooth grow/shrink curve (0 → 1 → 0) during the pulse, flat during the rest.
    const pulse =
      phase < MARKER_PULSE_PROPERTIES.pulseDurationInMs
        ? Math.sin(
            Math.PI * (phase / MARKER_PULSE_PROPERTIES.pulseDurationInMs)
          )
        : 0;
    const scale =
      MARKER_PULSE_PROPERTIES.highlightScale +
      MARKER_PULSE_PROPERTIES.popScale * pulse;

    graphic.symbol = new PictureMarkerSymbol(
      getHighlightMarkerSymbolProps(mapStyle, scale)
    );

    frameHandle = window.requestAnimationFrame(tick);
  }

  frameHandle = window.requestAnimationFrame(tick);

  return () => {
    if (frameHandle !== null) {
      window.cancelAnimationFrame(frameHandle);
      frameHandle = null;
    }
    graphic.symbol = getHighlightSymbol(mapStyle);
  };
}

/**
 * Fully hides (or restores) the underlying data-layer marker for a given entity so
 * it doesn't show behind the animated highlight overlay.
 *
 * Uses the layer view's `filter` to truly remove the feature from rendering
 * (equivalent to `display: none`). `featureEffect` is intentionally not used
 * because it has no effect while clustering (`featureReduction`) is enabled.
 *
 * Pass `null` to clear the filter and show all markers again. Standalone markers
 * are the only ones hidden this way; clustered features aren't rendered
 * individually, so there's nothing to hide for them.
 */
export async function setHiddenHighlightFeature(
  view: MapView,
  layer: GeoJSONLayer,
  entityId: string | null
): Promise<void> {
  const layerView = await view.whenLayerView(layer);
  if (!entityId) {
    layerView.filter = null;
    return;
  }
  const sanitizedId = entityId.replace(/'/g, "''");
  layerView.filter = new FeatureFilter({ where: `id <> '${sanitizedId}'` });
}

/** Queries the layer for a feature and returns its point geometry, or null. */
export async function getFeaturePoint(
  layer: GeoJSONLayer,
  entityId: string
): Promise<__esri.Point | null> {
  const sanitizedId = entityId.replace(/'/g, "''");
  const results = await layer.queryFeatures({
    where: `id = '${sanitizedId}'`,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by ArcGIS API
    returnGeometry: true,
  });
  return (results.features[0]?.geometry as __esri.Point | undefined) ?? null;
}

/** Returns the entity IDs of the features aggregated inside the given cluster graphic. */
export async function queryClusterMemberIds(
  view: MapView,
  layer: GeoJSONLayer,
  clusterGraphic: __esri.Graphic
): Promise<string[]> {
  try {
    const objectId = clusterGraphic.getObjectId();
    if (objectId === null || objectId === undefined) {
      return [];
    }
    const layerView = await view.whenLayerView(layer);
    const result = await layerView.queryFeatures({
      aggregateIds: [objectId],
    });
    return result.features.map((f) => String(f.attributes?.id)).filter(Boolean);
  } catch {
    return [];
  }
}

/** Returns the cluster graphic at the given point, or null if the marker is not clustered. */
export async function findClusterGraphic(
  view: MapView,
  layer: GeoJSONLayer,
  geometry: __esri.Point
): Promise<__esri.Graphic | null> {
  const screenPoint = view.toScreen(
    new Point({ longitude: geometry.longitude, latitude: geometry.latitude })
  );
  if (!screenPoint) {
    return null;
  }

  const hitResult = await view.hitTest(screenPoint, { include: [layer] });

  // Direct hit on a cluster
  const clusterHit = hitResult.results.find(
    (r) => r.type === "graphic" && isClusterGraphic(r.graphic)
  );
  if (clusterHit?.type === "graphic") {
    return clusterHit.graphic;
  }

  // Hit a non-cluster feature → the marker is standalone
  if (hitResult.results.some((r) => r.type === "graphic")) {
    return null;
  }

  // Feature not visible at its position → it's aggregated in a cluster elsewhere.
  // Query all visible clusters and return the nearest one using screen distance
  // (aggregates and features may use different spatial references).
  const layerView = await view.whenLayerView(layer);
  const aggregates = await layerView.queryAggregates({
    // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by ArcGIS API
    returnGeometry: true,
  });

  let nearest: __esri.Graphic | null = null;
  let minDistSq = Infinity;

  for (const feature of aggregates.features) {
    const clusterGeom = feature.geometry as __esri.Point;
    const clusterScreen = view.toScreen(
      new Point({
        longitude: clusterGeom.longitude,
        latitude: clusterGeom.latitude,
      })
    );
    if (!clusterScreen) {
      continue;
    }
    const dX = clusterScreen.x - screenPoint.x;
    const dY = clusterScreen.y - screenPoint.y;
    const distSq = dX * dX + dY * dY;
    if (distSq < minDistSq) {
      minDistSq = distSq;
      nearest = feature;
    }
  }

  return nearest;
}
